import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, MapPin, ShieldCheck, Star, Truck } from "lucide-react";
import SEO from "../../components/SEO";
import { ACTIVE_LOCATIONS, MEDIA } from "../../lib/constants";

export default function BestCannabisDispensary() {
  return (
    <>
      <SEO
        title="Best Cannabis Dispensary in Washington, DC | District Wellness Center"
        description="Looking for the best cannabis dispensary in Washington DC? District Wellness Center is a top-rated, licensed medical cannabis retailer on Georgia Ave NW. Premium flower, edibles, vapes, concentrates and same-day delivery."
        path="/best-cannabis-dispensary"
        canonical="/blog/best-cannabis-dispensary"
        ogType="article"
        breadcrumbs={[
          { name: "Blog", path: "/blog" },
          { name: "Best Cannabis Dispensary in DC", path: "/blog/best-cannabis-dispensary" },
        ]}
        article={{
          publishedTime: "2026-05-12",
          modifiedTime: "2026-09-15",
          section: "About DWC",
        }}
      />

      <section className="bg-ink text-white relative">
        <div className="grain absolute inset-0 opacity-50 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 sm:py-32 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-5">Patient Guide · Washington, DC</div>
            <h1 className="font-serif text-5xl sm:text-7xl leading-[0.95] tracking-tight">
              The <em className="text-brand">best cannabis dispensary</em> in Washington, DC.
            </h1>
            <p className="mt-6 text-white/75 text-lg leading-relaxed max-w-2xl">
              A practical, no-fluff guide to choosing a DC dispensary that's actually worth your time —
              and why District Wellness Center has become a go-to for medical patients across the District.
            </p>
          </div>
          <div className="lg:col-span-5">
            <img src={MEDIA.storefront} alt="District Wellness Center storefront in Washington DC" className="w-full h-80 lg:h-96 object-cover border border-white/10" />
          </div>
        </div>
      </section>

      <article className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-6 prose prose-lg font-sans text-ink-muted">
          <p className="text-xl leading-relaxed">
            Searching for the best cannabis dispensary in Washington, DC means filtering past the noise: too many shops on
            Georgia Ave, too many "deals" that aren't, too many counters that pressure you toward the highest-margin bag.
            A great dispensary does the opposite — it educates, curates, and lets you leave with exactly what you came for.
          </p>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">What separates a top-tier DC dispensary from the rest</h2>
          <ul className="list-disc pl-6 space-y-2">
            <li><strong>It's licensed.</strong> A real DC medical cannabis retailer is registered with the Office of Cannabis Administration (OCA). If you can't find a license, walk out.</li>
            <li><strong>Lab-tested products.</strong> Every batch should have a Certificate of Analysis available on request — for THC, terpenes, and contaminants.</li>
            <li><strong>Knowledgeable staff.</strong> Patient specialists who ask <em>what you're trying to solve</em> before recommending a SKU.</li>
            <li><strong>Curated selection over volume.</strong> 500 hand-picked SKUs &gt; 5,000 random ones.</li>
            <li><strong>Real same-day delivery.</strong> Inside DC, within an hour, dispatched from a real licensed location.</li>
          </ul>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">Why patients pick District Wellness Center</h2>
          <p>
            DWC is built around one idea: treat medical cannabis like the wellness category it is. Our shelves are filled
            with what we'd give our families — small-batch flower from licensed DC cultivators, measured-dose edibles,
            clean live rosin and live resin, and topicals & tinctures for non-smoking patients.
          </p>
          <p>
            We have a flagship location in Washington, DC — DWC Uptown at 4915 Georgia Ave NW. A curated menu of hand-picked
            products, knowledgeable patient specialists, and full DC ABCA compliance — License ABCA-127954.
          </p>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">DC dispensary checklist (save this)</h2>
          <ol className="list-decimal pl-6 space-y-2">
            <li>Confirm OCA license number is visible.</li>
            <li>Ask to see lab results on the product you're considering.</li>
            <li>Ask the budtender what the product <em>does</em>, not just its THC%.</li>
            <li>Compare prices on the same SKU — quality DC dispensaries are competitively priced.</li>
            <li>Test the delivery experience once. If it's chaotic, it'll always be chaotic.</li>
          </ol>

          <h2 className="font-serif text-3xl text-ink mt-14 mb-5">Areas we serve from our DC locations</h2>
          <p>
            DWC delivers same-day across <Link className="text-brand" to="/areas-served">21 DC neighborhoods</Link> — Petworth,
            Columbia Heights, Shaw, Adams Morgan, Dupont Circle, Georgetown, Capitol Hill, Navy Yard, Brightwood, Brookland,
            Tenleytown, Forest Hills, Takoma DC, plus select Maryland zones.
          </p>
        </div>
      </article>

      <section className="py-16 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-4">Why DWC</div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { icon: ShieldCheck, t: "Licensed & compliant" },
              { icon: Star, t: "Top-rated DC dispensary" },
              { icon: Truck, t: "Same-day delivery" },
              { icon: MapPin, t: "Georgia Ave dispensary" },
            ].map((x, i) => {
              const Icon = x.icon;
              return (
                <div key={i} className="p-6 bg-white border border-black/10">
                  <Icon size={20} className="text-brand mb-3" />
                  <div className="font-serif text-xl">{x.t}</div>
                </div>
              );
            })}
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-4">
            {ACTIVE_LOCATIONS.map((loc) => (
              <div key={loc.id} className="p-6 bg-white border border-black/10">
                <div className="text-[10px] uppercase tracking-[0.3em] text-brand mb-2">{loc.label}</div>
                <div className="font-serif text-xl leading-tight">{loc.street}</div>
                <div className="text-sm text-ink-muted">{loc.city}</div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/menu" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-600 text-white px-7 py-4 text-[12px] uppercase tracking-[0.22em]" data-testid="blog-menu-btn">
              See Our Menu <ArrowUpRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
